import React, { useContext, useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { ArrowLeft, Pencil, Building2, FileText, Upload, Trash2, ExternalLink, FileSpreadsheet, Download, Eye, Loader2, MapPin, ArrowUpDown, Users, Search } from 'lucide-react';
import { toast } from 'sonner';

import CustomerReportBuilder from './CustomerReportBuilder';
import ServiceAgreements from './ServiceAgreements';
import LocationImage from '@/components/common/LocationImage';
import LocationForm from './LocationForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LANGS = [['en', 'English'], ['es', 'Español']];
const langLabel = (c) => ({ en: 'English', es: 'Español' }[c] || (c ? c.toUpperCase() : 'Unspecified'));

const SpecBlocks = ({ blocks }) => (
  <div className="space-y-2" data-testid="spec-content">
    {blocks.map((b, i) => b.type === 'heading' ? (
      <div key={i} className={`font-bold text-[#0E2A47] ${b.level <= 1 ? 'text-base mt-3' : 'text-sm mt-2'}`}>{b.text}</div>
    ) : b.type === 'table' ? (
      <div key={i} className="overflow-auto"><table className="min-w-full border border-slate-200 text-xs">
        <tbody>{b.rows.map((r, ri) => <tr key={ri} className={ri === 0 ? 'bg-slate-50 font-semibold' : ''}>{r.map((c, ci) => <td key={ci} className="border border-slate-200 px-2 py-1 align-top text-slate-700">{c}</td>)}</tr>)}</tbody>
      </table></div>
    ) : (
      <p key={i} className="text-sm leading-relaxed text-slate-700">{b.text}</p>
    ))}
  </div>
);

const Row = ({ label, value }) => value ? (
  <div className="flex flex-col"><span className="text-[11px] uppercase tracking-wide text-slate-400">{label}</span><span className="text-sm text-[#0E2A47]">{value}</span></div>
) : null;

const _money = (v) => (v === null || v === undefined || v === '') ? null : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const _pct = (v) => (v === null || v === undefined || v === '') ? null : `${(Number(v) * 100).toFixed(2)}%`;
// A single budget figure: shows value, or a clear "Not allocated" when TALLY has no value.
const BudgetField = ({ label, amount, rate }) => (
  <div className="flex flex-col">
    <span className="text-[11px] uppercase tracking-wide text-slate-400">{label}</span>
    {amount === null || amount === undefined ? (
      <span className="text-sm italic text-slate-400">Not allocated</span>
    ) : (
      <span className="text-sm font-medium text-[#0E2A47]">{_money(amount)}{rate !== undefined && rate !== null ? <span className="ml-1 text-xs font-normal text-slate-400">({_pct(rate)})</span> : null}</span>
    )}
  </div>
);

const CustomerDetail = ({ customer, onBack, onEdit, canManage, initialTab = 'overview' }) => {
  const { token } = useContext(AuthContext);
  const h = { headers: { Authorization: `Bearer ${token}` } };
  const [locations, setLocations] = useState([]);
  const [awards, setAwards] = useState([]);
  const [docs, setDocs] = useState([]);
  const [specs, setSpecs] = useState([]);
  const [budget, setBudget] = useState({ budgets: [] });
  const [bdown, setBdown] = useState(null);           // per-location budget breakdown
  const [activeLoc, setActiveLoc] = useState(null);
  const [editLoc, setEditLoc] = useState(null);       // Service Location editor target
  const [showReport, setShowReport] = useState(false);
  const [personnel, setPersonnel] = useState([]);
  const [slSort, setSlSort] = useState('name');
  const [slDir, setSlDir] = useState('asc');
  const [slSearch, setSlSearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const cid = customer.id;
  const fileRef = useRef(null);
  const rootRef = useRef(null);
  useEffect(() => {
    // On narrow screens the detail renders below the module grid — bring it into
    // view immediately so tapping a customer clearly opens its record.
    try { rootRef.current?.scrollIntoView({ block: 'start' }); } catch { /* ignore */ }
    try { window.scrollTo(0, 0); } catch { /* ignore */ }
  }, []);
  const [pending, setPending] = useState({ category: 'attachment', location_id: '', language: 'en' });
  const [specLang, setSpecLang] = useState({});        // groupKey -> 'en'|'es'
  const [specContent, setSpecContent] = useState({});  // docId -> {ok,blocks,...} | 'loading'

  const loadDocs = () => {
    axios.get(`${API}/customer-docs/${cid}?category=attachment`, h).then(r => setDocs(r.data)).catch(() => {});
    axios.get(`${API}/customer-docs/${cid}?category=cleaning_spec`, h).then(r => setSpecs(r.data)).catch(() => {});
  };
  const loadLocations = () => axios.get(`${API}/customers/${cid}/locations`, h).then(r => setLocations(r.data || [])).catch(() => {});
  useEffect(() => {
    loadLocations();
    axios.get(`${API}/customers/${cid}/awards`, h).then(r => setAwards(r.data || [])).catch(() => {});
    axios.get(`${API}/customer-docs/${cid}/budget-breakdown`, h).then(r => setBdown(r.data)).catch(err => setBdown({ has_any: false, restricted: err?.response?.status === 403 }));
    axios.get(`${API}/location-personnel?customer_id=${cid}`, h).then(r => setPersonnel(r.data || [])).catch(() => {});
    loadDocs();
    // eslint-disable-next-line
  }, [cid, token]);

  const personnelByLoc = useMemo(() => {
    const m = {};
    personnel.forEach((p) => { if (p.location_id) (m[p.location_id] = m[p.location_id] || []).push(p); });
    return m;
  }, [personnel]);

  // Service locations list: exclude the client's main-account "location" (its account # equals
  // the customer account #, which is the client identifier, not a service location), then filter + sort.
  const visibleLocs = useMemo(() => {
    const mainAcct = String(customer.account_number || '');
    let list = locations.filter((l) => !mainAcct || String(l.account_number || '') !== mainAcct);
    if (slSearch) {
      const qq = slSearch.toLowerCase();
      list = list.filter((l) => `${l.location_name || ''} ${l.address || ''} ${l.city || ''} ${l.account_number || ''}`.toLowerCase().includes(qq));
    }
    const num = (s) => { const mm = String(s ?? '').match(/\d+/); return mm ? parseInt(mm[0], 10) : Number.POSITIVE_INFINITY; };
    const normName = (s) => String(s ?? '').replace(/[\u2010-\u2015]/g, '-').replace(/\s+/g, ' ').trim();
    const cmp = {
      name: (a, b) => normName(a.location_name).localeCompare(normName(b.location_name), undefined, { sensitivity: 'base', numeric: true }),
      address: (a, b) => (num(a.address) - num(b.address)) || (a.address || '').localeCompare(b.address || ''),
      account: (a, b) => String(a.account_number ?? '').localeCompare(String(b.account_number ?? ''), undefined, { numeric: true, sensitivity: 'base' }),
      sqft: (a, b) => (a.total_sq_ft || 0) - (b.total_sq_ft || 0),
    }[slSort] || (() => 0);
    list = [...list].sort(cmp);
    if (slDir === 'desc') list.reverse();
    return list;
  }, [locations, customer.account_number, slSearch, slSort, slDir]);

  // Group the (filtered, sorted) locations into subsections: the contracting customer's
  // own direct locations (AA=00) first, then one subsection per subaccount. Each carries
  // its organization name, account number and actual physical-location count.
  const locationGroups = useMemo(() => {
    const direct = { key: 'direct', name: customer.name, account_number: customer.account_number, locs: [] };
    const subs = {};
    visibleLocs.forEach((l) => {
      if (l.is_direct === false && l.subaccount_id) {
        const k = l.subaccount_account_number || l.subaccount_id;
        (subs[k] = subs[k] || { key: k, name: l.subaccount_name, account_number: l.subaccount_account_number, locs: [] }).locs.push(l);
      } else {
        direct.locs.push(l);
      }
    });
    const subList = Object.values(subs).sort((a, b) => String(a.account_number || '').localeCompare(String(b.account_number || ''), undefined, { numeric: true }));
    return [...(direct.locs.length ? [direct] : []), ...subList];
  }, [visibleLocs, customer.name, customer.account_number]);
  const hasSubaccounts = locationGroups.some((g) => g.key !== 'direct');

  const fileUrl = (d) => `${BACKEND_URL}/api/customer-docs/${cid}/${d.id}/file?auth=${token}`;
  const onPick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const b64 = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(file); });
    try {
      await axios.post(`${API}/customer-docs/${cid}/upload`, {
        filename: file.name, content_type: file.type || 'application/octet-stream', data_base64: b64,
        category: pending.category, location_id: pending.location_id || null,
        language: pending.category === 'cleaning_spec' ? pending.language : null,
      }, h);
      toast.success('Uploaded'); loadDocs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    e.target.value = '';
  };
  const del = async (d) => { try { await axios.delete(`${API}/customer-docs/${cid}/${d.id}`, h); toast.success('Deleted'); loadDocs(); } catch { toast.error('Delete failed'); } };
  const startUpload = (category) => { setPending((p) => ({ ...p, category })); fileRef.current?.click(); };

  // --- Cleaning specs grouping + in-app content ---
  const specGroups = useMemo(() => {
    const g = {};
    specs.forEach((d) => { const k = d.location_id || '__customer__'; (g[k] = g[k] || []).push(d); });
    return g;
  }, [specs]);

  const loadContent = async (doc, lang) => {
    const k = `${doc.id}:${lang}`;
    if (specContent[k]) return;
    setSpecContent((s) => ({ ...s, [k]: 'loading' }));
    try {
      const r = await axios.get(`${API}/customer-docs/${cid}/${doc.id}/content?lang=${lang}`, h);
      setSpecContent((s) => ({ ...s, [k]: r.data }));
    } catch {
      setSpecContent((s) => ({ ...s, [k]: { ok: false, reason: 'Could not load content.' } }));
    }
  };

  const DocList = ({ items, category, withLocation }) => (
    <div className="space-y-3" data-testid={`docs-${category}`}>
      {canManage && (
        <div className="flex flex-wrap items-center gap-2">
          {withLocation && (
            <select value={pending.location_id} onChange={(e) => setPending((p) => ({ ...p, location_id: e.target.value }))}
              className="min-h-10 rounded-lg border border-slate-300 px-2 text-sm" data-testid="spec-location-select">
              <option value="">(Customer-wide)</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.location_name}</option>)}
            </select>
          )}
          <button onClick={() => startUpload(category)} data-testid={`upload-${category}-btn`}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0E2A47] px-3 py-2 text-sm font-semibold text-white hover:bg-[#16324F]"><Upload size={15} /> Upload</button>
        </div>
      )}
      {items.length === 0 ? <p className="text-sm text-slate-400">No documents yet.</p> : items.map((d) => (
        <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
          <a href={fileUrl(d)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm font-medium text-[#0E2A47] hover:underline">
            <FileText size={16} /> {d.title || d.filename}
          </a>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            {withLocation && d.location_id && <span>{locations.find((l) => l.id === d.location_id)?.location_name || 'Property'}</span>}
            <span>{Math.round((d.size || 0) / 1024)} KB</span>
            {canManage && <button onClick={() => del(d)} data-testid={`doc-del-${d.id}`} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>}
          </div>
        </div>
      ))}
    </div>
  );

  useEffect(() => {
    Object.values(specGroups).forEach((items) => {
      const src = items[0];
      if (src) loadContent(src, (src.language || 'en'));
    });
    // eslint-disable-next-line
  }, [specs]);

  if (showReport) {
    return <CustomerReportBuilder customerId={cid} customerName={customer.name} onBack={() => setShowReport(false)} />;
  }

  if (activeLoc) {
    const l = activeLoc;
    return (
      <div className="mx-auto max-w-2xl space-y-4 p-4" data-testid="location-detail">
        <button onClick={() => setActiveLoc(null)} className="inline-flex items-center gap-1 text-sm text-slate-500"><ArrowLeft size={15} /> Back to service locations</button>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <LocationImage locationId={l.id} imageUrl={l.image_url} name={l.location_name} className="h-16 w-24" testId={`location-detail-image-${l.id}`} />
            <div>
              <h2 className="text-xl font-bold text-[#0E2A47]">{l.location_name}</h2>
              <div className="font-mono text-xs text-slate-500">Account # {l.account_number || '—'}</div>
            </div>
          </div>
          {canManage && <button onClick={() => setEditLoc(l)} data-testid="location-edit-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-[#0E2A47] px-3 py-2 text-sm font-semibold text-white hover:bg-[#16324F]"><Pencil size={15} /> Edit</button>}
        </div>
        <div className="grid grid-cols-2 gap-4 rounded-lg border border-slate-200 bg-white p-4">
          <Row label="Account #" value={l.account_number} />
          <Row label="Location Code" value={l.location_code} />
          <Row label="Building / Unit #" value={l.building_number || '—'} />
          <Row label="Address" value={l.address} /><Row label="City" value={l.city} />
          <Row label="State" value={l.state} /><Row label="ZIP" value={l.zip} />
          <Row label="County" value={l.county} />
          <Row label="Phone" value={l.contact_number} /><Row label="On-site Contact" value={l.local_rep_name} />
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="location-personnel">
          <div className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#0E2A47]"><Users size={15} /> Assigned Personnel ({(personnelByLoc[l.id] || []).length})</div>
          {(personnelByLoc[l.id] || []).length === 0 ? (
            <p className="text-sm text-slate-400">No personnel assigned to this service location.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {(personnelByLoc[l.id] || []).map((p) => (
                <div key={p.id} className="flex items-center justify-between py-2" data-testid={`loc-person-${p.id}`}>
                  <div>
                    <div className="text-sm font-medium text-[#0E2A47]">{p.name}{p.is_primary ? <span className="ml-2 rounded-full bg-[#8FC93A]/20 px-2 py-0.5 text-[10px] font-semibold text-[#4d7c0f]">Primary</span> : null}</div>
                    <div className="text-xs text-slate-500">{[p.assignment_role, p.relationship_type].filter(Boolean).join(' · ')}{p.employee_identifier ? ` · #${p.employee_identifier}` : ''}</div>
                  </div>
                  <div className="text-right text-xs text-slate-400">{p.phone || ''}{p.email ? <div className="truncate">{p.email}</div> : null}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        {editLoc && (
          <LocationForm location={editLoc} customers={[{ id: cid, name: customer.name }]} defaultCustomerId={cid}
            onClose={() => setEditLoc(null)}
            onSaved={() => { loadLocations(); axios.get(`${API}/customers/${cid}/locations`, h).then(r => { const u = (r.data || []).find(x => x.id === l.id); if (u) setActiveLoc(u); }); }} />
        )}
      </div>
    );
  }

  return (
    <div ref={rootRef} className="mx-auto max-w-4xl space-y-4 p-4" data-testid="customer-detail">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-slate-500" data-testid="detail-back"><ArrowLeft size={15} /> Back</button>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowReport(true)} data-testid="prepare-report-btn"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-[#0E2A47] hover:bg-slate-50">Prepare Report</button>
          {canManage && <button onClick={onEdit} data-testid="detail-edit-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-[#0E2A47] px-3 py-2 text-sm font-semibold text-white hover:bg-[#16324F]"><Pencil size={15} /> Edit</button>}
        </div>
      </div>
      <h1 className="text-2xl font-bold text-[#0E2A47]">{customer.name}</h1>
      <input ref={fileRef} type="file" className="hidden" onChange={onPick} data-testid="doc-file-input" />

      <Tabs defaultValue={initialTab}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
          <TabsTrigger value="properties" data-testid="tab-properties">Service Locations</TabsTrigger>
          <TabsTrigger value="agreements" data-testid="tab-agreements">Service Agreements</TabsTrigger>
          <TabsTrigger value="budget" data-testid="tab-budget">Current Budget</TabsTrigger>
          <TabsTrigger value="specs" data-testid="tab-specs">Cleaning Specs</TabsTrigger>
          <TabsTrigger value="attachments" data-testid="tab-attachments">Attachments</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-2 gap-4 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-3">
            <Row label="Type" value={customer.type} /><Row label="Industry" value={customer.industry} /><Row label="Status" value={customer.status} />
            <Row label="Organization Phone" value={customer.phone} /><Row label="General Email" value={customer.email} /><Row label="Website" value={customer.website} />
            <Row label="Primary Contact" value={customer.primary_contact_name} />
            <Row label="Primary Email" value={customer.primary_contact_email} /><Row label="Primary Phone" value={customer.primary_contact_phone} />
            <Row label="Billing Address" value={[customer.billing_address, customer.billing_city, customer.billing_state, customer.billing_zip].filter(Boolean).join(', ')} />
          </div>
          {customer.notes && <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4"><Row label="Notes" value={customer.notes} /></div>}
        </TabsContent>

        <TabsContent value="contacts" className="mt-4">
          <div className="space-y-3">
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="text-xs font-bold uppercase text-[#8FC93A]">Primary / Main Contact</div>
              <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-3">
                <Row label="Name" value={customer.primary_contact_name || customer.main_contact_name} />
                <Row label="Title" value={customer.main_contact_title} />
                <Row label="Email" value={customer.primary_contact_email || customer.main_contact_email} />
                <Row label="Phone" value={customer.primary_contact_phone || customer.main_contact_phone} />
                <Row label="Billing Contact" value={customer.billing_contact_name} />
                <Row label="Billing Email" value={customer.billing_contact_email} />
              </div>
            </div>
            {(customer.additional_contacts || []).length > 0 && (
              <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="detail-additional-contacts">
                <div className="text-xs font-bold uppercase text-[#8FC93A]">Other Contacts</div>
                <div className="mt-2 space-y-1">
                  {customer.additional_contacts.map((c, i) => <div key={i} className="text-sm text-slate-600">{[c.name, c.title, c.email, c.phone].filter(Boolean).join(' · ')}</div>)}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="properties" className="mt-4">
          <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="sl-controls">
            <div className="relative flex-1 min-w-[180px]">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={slSearch} onChange={(e) => setSlSearch(e.target.value)} placeholder="Filter locations…" data-testid="sl-search"
                className="w-full rounded-lg border border-slate-200 py-2 pl-8 pr-3 text-sm" />
            </div>
            <select value={slSort} onChange={(e) => setSlSort(e.target.value)} data-testid="sl-sort"
              className="min-h-9 rounded-lg border border-slate-200 px-2 text-sm">
              <option value="name">Sort: Alphabetical</option>
              <option value="address">Sort: Address</option>
              <option value="account">Sort: Account #</option>
              <option value="sqft">Sort: Square Footage</option>
            </select>
            <button onClick={() => setSlDir((d) => d === 'asc' ? 'desc' : 'asc')} data-testid="sl-dir"
              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-2 text-sm text-slate-600 hover:bg-slate-50">
              <ArrowUpDown size={14} /> {slDir === 'asc' ? 'Asc' : 'Desc'}
            </button>
          </div>
          {visibleLocs.length === 0 ? <p className="text-sm text-slate-400" data-testid="sl-empty">No service locations match.</p> : (
            <div className="space-y-5" data-testid="sl-list">
              {locationGroups.map((g) => {
                const collapsed = !!collapsedGroups[g.key];
                const isSub = g.key !== 'direct';
                return (
                  <div key={g.key} data-testid={`sl-group-${g.key}`} className="min-w-0">
                    {(hasSubaccounts || isSub) && (
                      <button type="button" onClick={() => setCollapsedGroups((s) => ({ ...s, [g.key]: !s[g.key] }))}
                        data-testid={`sl-group-toggle-${g.key}`}
                        className="mb-2 flex w-full flex-wrap items-center justify-between gap-x-2 gap-y-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-left hover:bg-slate-100">
                        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                          <Building2 size={15} className="flex-shrink-0 text-[#0E2A47]" />
                          <span className="font-semibold text-[#0E2A47] break-words">{g.name || 'Direct'}</span>
                          <span className="flex-shrink-0 rounded-full bg-[#0E2A47] px-2 py-0.5 font-mono text-[10px] font-semibold text-white">{g.account_number || '—'}</span>
                          {isSub && <span className="hidden flex-shrink-0 rounded-full bg-[#8FC93A]/20 px-2 py-0.5 text-[10px] font-semibold text-[#4d7c0f] sm:inline-flex">Subaccount</span>}
                        </span>
                        <span className="flex flex-shrink-0 items-center gap-1 text-xs text-slate-500">
                          {g.locs.length} location{g.locs.length === 1 ? '' : 's'} <ArrowUpDown size={12} className={`transition-transform ${collapsed ? 'rotate-180' : ''}`} />
                        </span>
                      </button>
                    )}
                    {!collapsed && (
                      <div className="grid gap-2">
                        {g.locs.map((l) => {
                          const ppl = personnelByLoc[l.id] || [];
                          return (
                            <button key={l.id} onClick={() => setActiveLoc(l)} data-testid={`property-row-${l.id}`}
                              className="flex w-full min-w-0 items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 text-left hover:border-[#8FC93A]">
                              <LocationImage locationId={l.id} imageUrl={l.image_url} name={l.location_name} className="h-12 w-16 flex-shrink-0" testId={`sl-image-${l.id}`} iconSize={16} />
                              <div className="min-w-0 flex-1">
                                <div className="truncate font-semibold text-[#0E2A47]">{l.location_name}</div>
                                <div className="truncate text-xs text-slate-500">{[l.address, l.city, l.state, l.zip].filter(Boolean).join(', ')}{l.total_sq_ft ? ` · ${Number(l.total_sq_ft).toLocaleString()} sq ft` : ''}</div>
                                <div className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-400" data-testid={`sl-personnel-count-${l.id}`}>
                                  <Users size={11} /> {ppl.length ? `${ppl.length} assigned: ${ppl.slice(0, 2).map((p) => p.name).join(', ')}${ppl.length > 2 ? '…' : ''}` : 'No personnel assigned'}
                                </div>
                              </div>
                              <span className="flex-shrink-0 font-mono text-xs text-slate-400" data-testid={`sl-acct-${l.id}`}>{l.account_number || '—'}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="agreements" className="mt-4">
          <ServiceAgreements customer={customer} locations={locations} canManage={canManage} />
        </TabsContent>

        <TabsContent value="budget" className="mt-4">
          {!bdown ? (
            <p className="text-sm text-slate-400">Loading budget…</p>
          ) : bdown.restricted ? (
            <p className="text-sm text-slate-500" data-testid="budget-restricted">Financial budget information requires Contract Budgets (TALLY) access.</p>
          ) : !bdown.has_any ? (
            <p className="text-sm text-slate-400" data-testid="budget-empty">No current approved budget in TALLY for this customer.</p>
          ) : (
            <div className="space-y-3" data-testid="budget-breakdown">
              <p className="text-xs text-slate-500">Budget figures below come from the current approved <span className="font-semibold">TALLY</span> budget. Read-only here — use the TALLY link to edit.</p>
              {(bdown.account_budgets || []).map((b) => {
                const f = b.fields;
                return (
                  <div key={b.budget_id} className="rounded-lg border border-[#0E2A47]/20 bg-[#0E2A47]/[0.03] p-4" data-testid={`budget-account-${b.budget_id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2 font-semibold text-[#0E2A47]"><FileSpreadsheet size={16} /> {b.name}
                          <span className="rounded-full bg-[#0E2A47] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">Account-level</span>
                        </div>
                        <div className="font-mono text-xs text-slate-500">Customer account # {b.account_number || '—'} · links to the customer record (not a single service location)</div>
                        <div className="mt-0.5 text-xs text-slate-400">TALLY: {b.name} · v{b.version} · {b.status} · {b.period || '—'}</div>
                      </div>
                      <a href={b.tally_link} className="inline-flex items-center gap-1 rounded-lg border border-[#8FC93A] px-3 py-1.5 text-sm font-semibold text-[#4d7c0f] hover:bg-[#8FC93A]/10" data-testid={`budget-open-${b.budget_id}`}>Open in TALLY <ExternalLink size={14} /></a>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-slate-100 pt-3 sm:grid-cols-3">
                      <BudgetField label="Monthly Billing" amount={f.monthly_billing} />
                      <BudgetField label="Direct Labor" amount={f.total_base_labor} />
                      <BudgetField label="Payroll Costs" amount={f.payroll_costs} />
                      <BudgetField label="Uniforms" amount={f.uniforms} />
                      <BudgetField label="Other Direct Costs" amount={f.non_labor_direct} />
                      <BudgetField label="Total Monthly Costs" amount={f.total_monthly_costs} />
                      <BudgetField label="Company Overhead" amount={f.overhead_amount} rate={f.overhead_rate} />
                      <BudgetField label="Company Retainage" amount={f.retainage_amount} rate={f.retainage_rate} />
                      <BudgetField label="Profit" amount={f.profit_amount} rate={f.profit_rate} />
                      <BudgetField label="Fully Allocated Profit" amount={f.fully_allocated_profit} rate={f.fully_allocated_profit_rate} />
                    </div>
                  </div>
                );
              })}
              {bdown.locations.filter((e) => e.has_budget).map((e) => {
                const b = e.budget; const f = b.fields;
                return (
                  <div key={e.location_id} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`budget-loc-${e.location_id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2 font-semibold text-[#0E2A47]"><FileSpreadsheet size={16} /> {e.location_name}</div>
                        <div className="font-mono text-xs text-slate-500">Account # {e.account_number || '—'}</div>
                        <div className="mt-0.5 text-xs text-slate-400">TALLY: {b.name} · v{b.version} · {b.status} · {b.period || '—'}</div>
                      </div>
                      <a href={b.tally_link} className="inline-flex items-center gap-1 rounded-lg border border-[#8FC93A] px-3 py-1.5 text-sm font-semibold text-[#4d7c0f] hover:bg-[#8FC93A]/10" data-testid={`budget-open-${b.budget_id}`}>Open in TALLY <ExternalLink size={14} /></a>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 border-t border-slate-100 pt-3 sm:grid-cols-3">
                      <BudgetField label="Monthly Billing" amount={f.monthly_billing} />
                      <BudgetField label="Direct Labor" amount={f.total_base_labor} />
                      <BudgetField label="Payroll Costs" amount={f.payroll_costs} />
                      <BudgetField label="Uniforms" amount={f.uniforms} />
                      <BudgetField label="Other Direct Costs" amount={f.non_labor_direct} />
                      <BudgetField label="Total Monthly Costs" amount={f.total_monthly_costs} />
                      <BudgetField label="Company Overhead" amount={f.overhead_amount} rate={f.overhead_rate} />
                      <BudgetField label="Company Retainage" amount={f.retainage_amount} rate={f.retainage_rate} />
                      <BudgetField label="Profit" amount={f.profit_amount} rate={f.profit_rate} />
                      <BudgetField label="Fully Allocated Profit" amount={f.fully_allocated_profit} rate={f.fully_allocated_profit_rate} />
                    </div>
                  </div>
                );
              })}
              {bdown.unmatched.length > 0 && bdown.unmatched.map((b) => (
                <div key={b.budget_id} className="rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid={`budget-unmatched-${b.budget_id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm text-[#0E2A47]"><span className="font-semibold">{b.name}</span> <span className="font-mono text-xs text-slate-500">Acct # {b.account_number || '—'}</span> — TALLY budget not linked to a service location</div>
                    <a href={b.tally_link} className="inline-flex items-center gap-1 text-sm font-semibold text-[#4d7c0f] hover:underline">Open in TALLY <ExternalLink size={14} /></a>
                  </div>
                </div>
              ))}
              {bdown.locations.filter((e) => !e.has_budget).length > 0 && (
                <details className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="budget-unallocated">
                  <summary className="cursor-pointer text-sm font-semibold text-slate-600">Service locations without a current TALLY allocation ({bdown.locations.filter((e) => !e.has_budget).length})</summary>
                  <div className="mt-2 space-y-1">
                    {bdown.locations.filter((e) => !e.has_budget).map((e) => (
                      <div key={e.location_id} className="flex items-center justify-between text-xs text-slate-500">
                        <span>{e.location_name} <span className="font-mono">· Acct # {e.account_number || '—'}</span></span>
                        <span className="italic">Not allocated in TALLY</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="specs" className="mt-4">
          <div className="space-y-4" data-testid="specs-panel">
            {canManage && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <select value={pending.location_id} onChange={(e) => setPending((p) => ({ ...p, location_id: e.target.value }))}
                  className="min-h-10 rounded-lg border border-slate-300 px-2 text-sm" data-testid="spec-location-select">
                  <option value="">(Customer-wide)</option>
                  {locations.map((l) => <option key={l.id} value={l.id}>{l.location_name}</option>)}
                </select>
                <select value={pending.language} onChange={(e) => setPending((p) => ({ ...p, language: e.target.value }))}
                  className="min-h-10 rounded-lg border border-slate-300 px-2 text-sm" data-testid="spec-language-select">
                  {LANGS.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
                </select>
                <button onClick={() => startUpload('cleaning_spec')} data-testid="upload-cleaning_spec-btn"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#0E2A47] px-3 py-2 text-sm font-semibold text-white hover:bg-[#16324F]"><Upload size={15} /> Upload spec (Word)</button>
                <span className="text-xs text-slate-400">Word (.docx) specs are readable on screen.</span>
              </div>
            )}
            {Object.keys(specGroups).length === 0 ? <p className="text-sm text-slate-400">No cleaning specifications yet.</p> : (
              Object.entries(specGroups).map(([key, items]) => {
                const label = key === '__customer__' ? 'Customer-wide' : (locations.find((l) => l.id === key)?.location_name || 'Property');
                const byLang = {}; items.forEach((d) => { byLang[(d.language || 'en')] = d; });
                const defaultLang = items[0]?.language || 'en';
                const sel = specLang[key] || defaultLang;
                const srcDoc = byLang[sel] || items[0];                 // prefer an uploaded version in the selected language
                const c = srcDoc ? specContent[`${srcDoc.id}:${sel}`] : null;
                return (
                  <div key={key} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`spec-group-${key}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2 font-semibold text-[#0E2A47]"><FileText size={16} /> {label}</div>
                      <div className="flex items-center gap-1 rounded-lg border border-slate-200 p-0.5">
                        {LANGS.map(([code, lbl]) => (
                          <button key={code} data-testid={`spec-lang-${key}-${code}`}
                            onClick={() => { setSpecLang((s) => ({ ...s, [key]: code })); const sd = byLang[code] || items[0]; if (sd) loadContent(sd, code); }}
                            className={`rounded-md px-3 py-1 text-xs font-semibold ${sel === code ? 'bg-[#0E2A47] text-white' : 'text-[#0E2A47] hover:bg-slate-100'}`}>{lbl}</button>
                        ))}
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">{srcDoc.title || srcDoc.filename}</span>
                          {c && c !== 'loading' && c.translated && (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`spec-mt-badge-${key}`}>
                              Machine-translated from {langLabel(c.source_language)} — not an uploaded document
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <a href={fileUrl(srcDoc)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-[#0E2A47] hover:underline" data-testid={`spec-download-${srcDoc.id}`}><Download size={13} /> Download original ({langLabel(srcDoc.language)})</a>
                          {canManage && <button onClick={() => del(srcDoc)} className="text-red-500 hover:text-red-700" data-testid={`spec-del-${srcDoc.id}`}><Trash2 size={14} /></button>}
                        </div>
                      </div>
                      {!c || c === 'loading' ? (
                        <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> {sel !== (srcDoc.language || 'en') ? 'Translating…' : 'Loading…'}</div>
                      ) : !c.ok ? (
                        <p className="text-sm text-[#C8641E]">{c.reason || 'Cannot display content — please download the original.'}</p>
                      ) : (
                        <div className="max-h-[520px] overflow-auto rounded-lg border border-slate-100 bg-slate-50/50 p-3" data-testid={`spec-viewer-${srcDoc.id}`}><SpecBlocks blocks={c.blocks} /></div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </TabsContent>
        <TabsContent value="attachments" className="mt-4"><DocList items={docs} category="attachment" /></TabsContent>
      </Tabs>
    </div>
  );
};

export default CustomerDetail;
